/**
 * History Page - Redesigned
 * Shows unified recent history across all supported game categories.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api, { chessAPI, checkersAPI, draughtsAPI, gameAPI } from '../services/api';
import multiplayerAPI from '../services/multiplayerAPI';

import Footer from '../components/Footer';
import '../styles/Dashboard.css';

const HISTORY_WINDOW_DAYS = 31;
const HISTORY_CATEGORIES = new Set(['dice', 'lucky', 'flappy', 'chess', 'checkers', 'draughts', 'color', 'whot']);
const HISTORY_CATEGORY_ORDER = ['dice', 'lucky', 'flappy', 'chess', 'checkers', 'draughts', 'color', 'whot'];

const toDateInput = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const getTodayDateInput = () => toDateInput(new Date());

const getWindowStartDateInput = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - (HISTORY_WINDOW_DAYS - 1));
  return toDateInput(d);
};

const normalizeCategory = (raw) => {
  const text = String(raw || '').trim().toLowerCase();
  if (!text || text === 'default') return 'dice';
  if (text === 'reaction' || text === 'color_confusion' || text === 'color') return 'color';
  if (text === 'lucky_dice' || text === 'lucky') return 'lucky';
  return text;
};

const categoryLabel = (category) => {
  const key = normalizeCategory(category);
  if (key === 'dice') return 'Dice';
  if (key === 'lucky') return 'Lucky';
  if (key === 'flappy') return 'Flappy';
  if (key === 'chess') return 'Chess';
  if (key === 'checkers') return 'Checkers';
  if (key === 'draughts') return 'Draughts';
  if (key === 'color') return 'Color';
  if (key === 'whot') return 'WHOT';
  return key || 'Game';
};

const categoryIcon = (category) => {
  const key = normalizeCategory(category);
  if (key === 'dice') return '🎲';
  if (key === 'lucky') return '🍀';
  if (key === 'flappy') return '🐦';
  if (key === 'chess') return '♟️';
  if (key === 'checkers') return '⬛';
  if (key === 'draughts') return '🏁';
  if (key === 'color') return '🎨';
  if (key === 'whot') return '🃏';
  return '🎮';
};

const categoryColor = (category) => {
  const key = normalizeCategory(category);
  if (key === 'dice') return '#f59e0b';
  if (key === 'lucky') return '#8b5cf6';
  if (key === 'flappy') return '#10b981';
  if (key === 'chess') return '#6366f1';
  if (key === 'checkers') return '#d97706';
  if (key === 'draughts') return '#7c3aed';
  if (key === 'color') return '#ec4899';
  if (key === 'whot') return '#ef4444';
  return '#94a3b8';
};

const toEventDate = (record) => {
  const raw =
    record?.completed_at ||
    record?.completedAt ||
    record?.paid_at ||
    record?.created_at ||
    record?.createdAt ||
    null;
  if (!raw) return null;
  const dt = new Date(raw);
  return Number.isNaN(dt.getTime()) ? null : dt;
};

const formatEventDate = (dateValue) => {
  const dt = dateValue instanceof Date ? dateValue : new Date(dateValue);
  if (Number.isNaN(dt.getTime())) return 'Unknown date';
  return dt.toLocaleString();
};

const formatRelativeTime = (dateValue) => {
  const dt = dateValue instanceof Date ? dateValue : new Date(dateValue);
  if (Number.isNaN(dt.getTime())) return null;
  
  const now = new Date();
  const diffMs = now - dt;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return null;
};

const History = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const userId = Number(user?.id);
  const hasUserId = Number.isInteger(userId) && userId > 0;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterError, setFilterError] = useState('');
  const [searchText, setSearchText] = useState('');
  const [dateFrom, setDateFrom] = useState(getWindowStartDateInput());
  const [dateTo, setDateTo] = useState(getTodayDateInput());
  const [activeCategory, setActiveCategory] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'

  const [games, setGames] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [multiplayerGames, setMultiplayerGames] = useState([]);
  const [chessHistory, setChessHistory] = useState([]);
  const [checkersHistory, setCheckersHistory] = useState([]);
  const [draughtsHistory, setDraughtsHistory] = useState([]);
  const chargePercent = 7;

  const buildPotLabel = (grossPot) => {
    const total = Number(grossPot) || 0;
    const charge = Math.floor((total * chargePercent) / 100);
    const net = Math.max(0, total - charge);
    return `Pot: ${total} | Fee: ${charge} | Net: ${net}`;
  };

  const getChessResultLabel = (match, playerId) => {
    const winnerId = Number(match.winner_id || 0);
    if (!winnerId) return 'Draw';
    if (!playerId) return 'Loss';
    return winnerId === Number(playerId) ? 'Win' : 'Loss';
  };

  const getChessResultColor = (match, playerId) => {
    const winnerId = Number(match.winner_id || 0);
    if (!winnerId) return '#fbbf24';
    if (!playerId) return '#ef4444';
    return winnerId === Number(playerId) ? '#10b981' : '#ef4444';
  };

  const buildChessPlayers = (match) => {
    const whiteId = match.white_id || match.creator_id;
    const blackId = match.black_id || match.opponent_id;
    const whiteName = match.white_username || match.creator_username || (whiteId ? `Player ${whiteId}` : 'White');
    const blackName = match.black_username || match.opponent_username || (blackId ? `Player ${blackId}` : 'Black');

    const players = [];
    players.push({ id: whiteId, name: whiteName, side: 'White', opponentName: blackName });
    players.push({ id: blackId, name: blackName, side: 'Black', opponentName: whiteName });
    return players;
  };

  const historyItems = useMemo(() => {
    const diceItems = games.map((g) => {
      const p1 = g.player1_username || `Player ${g.player1_id}`;
      const p2 = g.player2_username || `Player ${g.player2_id}`;
      const winner = g.winner_id ? (g.winner_id === g.player1_id ? p1 : p2) : 'Tie';
      const wager = Number(g.wager || 0);
      return {
        key: `dice-game-${g.id}`,
        category: 'dice',
        id: g.id,
        title: `${p1} vs ${p2}`,
        subtitle: `Wager ${wager}`,
        details: `${buildPotLabel(wager * 2)} | Winner: ${winner}`,
        date: toEventDate(g),
        viewPath: `/game/${g.id}`,
        status: winner !== 'Tie' ? 'Completed' : 'Draw',
        result: winner !== 'Tie' ? (winner === p1 ? 'Win' : 'Loss') : 'Draw'
      };
    });

    const challengeItems = challenges
      .map((c) => {
        const category = normalizeCategory(c.game || 'dice');
        if (!HISTORY_CATEGORIES.has(category)) return null;
        const seriesGames = Number(c.num_games || 1);
        const wager = Number(c.wager || 0);
        return {
          key: `challenge-${c.id}`,
          category,
          id: c.id,
          title: `${c.creator_username} vs ${c.opponent_username}`,
          subtitle: `Series: ${seriesGames} games | Wager: ${wager}`,
          details: `${buildPotLabel(wager * seriesGames * 2)} | Status: ${c.status}`,
          date: toEventDate(c),
          viewPath: category === 'whot' ? `/whot/results/${c.id}` : `/challenges/${c.id}`,
          status: c.status,
          result: c.status === 'completed' ? 'Completed' : 'Pending'
        };
      })
      .filter(Boolean);

    const multiplayerItems = multiplayerGames
      .map((m) => {
        const category = normalizeCategory(m.game || 'dice');
        if (!HISTORY_CATEGORIES.has(category)) return null;

        const currentPlayers = Number(m.current_players || 0);
        const maxPlayers = Number(m.max_players || 0);
        const wager = Number(m.wager || 0);
        return {
          key: `multi-${m.id}`,
          category,
          id: m.id,
          title: `${currentPlayers}/${maxPlayers} Players`,
          subtitle: wager > 0 ? `Wager: ${wager}` : 'Friendly match',
          details: wager > 0 ? buildPotLabel(wager * Math.max(currentPlayers, 1)) : 'No wager',
          date: toEventDate(m),
          viewPath: category === 'whot' ? `/whot/results/${m.id}` : `/multiplayer/game/${m.id}`,
          status: 'Completed',
          result: 'Played'
        };
      })
      .filter(Boolean);

    const chessItems = chessHistory.map((match) => {
      const players = buildChessPlayers(match);
      const currentPlayer = players.find((p) => Number(p.id || 0) === userId) || players[0];
      const wager = Number(match.wager || 0);
      const modeLabel = match.mode === 'betting' ? 'Betting' : 'Friendly';
      const result = getChessResultLabel(match, currentPlayer?.id);
      return {
        key: `chess-${match.id}`,
        category: 'chess',
        id: match.id,
        title: `${currentPlayer?.name || 'Player'} (${currentPlayer?.side || 'Side'}) vs ${currentPlayer?.opponentName || 'Opponent'}`,
        subtitle: `${modeLabel} mode${wager > 0 ? ` | Wager: ${wager}` : ''}`,
        details: wager > 0 ? buildPotLabel(wager * 2) : 'No wager',
        date: toEventDate(match),
        viewPath: `/chess/duel/${match.id}`,
        status: result === 'Draw' ? 'Draw' : 'Completed',
        result: result
      };
    });

    const buildBoardGameItems = (history, gameKey, duelPath) =>
      history.map((match) => {
        const creatorName = match.creator_username || `Player ${match.creator_id}`;
        const opponentName = match.opponent_username || `Player ${match.opponent_id}`;
        const winnerId = Number(match.winner_id || 0);
        const isCreator = Number(match.creator_id) === userId;
        const myName = isCreator ? creatorName : opponentName;
        const theirName = isCreator ? opponentName : creatorName;
        const wager = Number(match.wager || 0);
        const modeLabel = match.mode === 'betting' ? 'Betting' : 'Friendly';
        let result = 'Draw';
        if (winnerId) result = winnerId === userId ? 'Win' : 'Loss';
        return {
          key: `${gameKey}-${match.id}`,
          category: gameKey,
          id: match.id,
          title: `${myName} vs ${theirName}`,
          subtitle: `${modeLabel} mode${wager > 0 ? ` | Wager: ${wager}` : ''}`,
          details: wager > 0 ? buildPotLabel(wager * 2) : 'No wager',
          date: toEventDate(match),
          viewPath: `/${duelPath}/duel/${match.id}`,
          status: result === 'Draw' ? 'Draw' : 'Completed',
          result
        };
      });

    const checkersItems = buildBoardGameItems(checkersHistory, 'checkers', 'checkers');
    const draughtsItems = buildBoardGameItems(draughtsHistory, 'draughts', 'draughts');

    let items = [...diceItems, ...challengeItems, ...multiplayerItems, ...chessItems, ...checkersItems, ...draughtsItems]
      .filter((item) => item && HISTORY_CATEGORIES.has(normalizeCategory(item.category)));

    // Apply sorting
    if (sortBy === 'newest') {
      items.sort((a, b) => {
        const ta = a.date instanceof Date ? a.date.getTime() : 0;
        const tb = b.date instanceof Date ? b.date.getTime() : 0;
        return tb - ta;
      });
    } else if (sortBy === 'oldest') {
      items.sort((a, b) => {
        const ta = a.date instanceof Date ? a.date.getTime() : 0;
        const tb = b.date instanceof Date ? b.date.getTime() : 0;
        return ta - tb;
      });
    } else if (sortBy === 'category') {
      items.sort((a, b) => a.category.localeCompare(b.category));
    }

    return items;
  }, [games, challenges, multiplayerGames, chessHistory, checkersHistory, draughtsHistory, userId, sortBy]);

  const filteredItems = useMemo(() => {
    const fromDate = dateFrom ? new Date(`${dateFrom}T00:00:00`) : null;
    const toDate = dateTo ? new Date(`${dateTo}T23:59:59.999`) : null;
    const query = String(searchText || '').trim().toLowerCase();

    return historyItems.filter((item) => {
      const normalizedItemCategory = normalizeCategory(item.category);

      if (activeCategory !== 'all' && normalizedItemCategory !== activeCategory) {
        return false;
      }

      if (fromDate || toDate) {
        if (!(item.date instanceof Date)) return false;
        if (fromDate && item.date < fromDate) return false;
        if (toDate && item.date > toDate) return false;
      }

      if (!query) return true;

      const haystack = [
        categoryLabel(normalizedItemCategory),
        String(item.id || ''),
        item.title,
        item.subtitle,
        item.details,
      ]
        .join(' ')
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [historyItems, searchText, dateFrom, dateTo, activeCategory]);

  const applyDateRange = (nextFrom, nextTo) => {
    const minAllowed = new Date(`${getWindowStartDateInput()}T00:00:00`);
    const maxAllowed = new Date(`${getTodayDateInput()}T23:59:59.999`);

    const from = new Date(`${nextFrom}T00:00:00`);
    const to = new Date(`${nextTo}T23:59:59.999`);

    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
      setFilterError('Please choose a valid date range.');
      return;
    }
    if (from > to) {
      setFilterError('From date cannot be after To date.');
      return;
    }
    if (from < minAllowed || to > maxAllowed) {
      setFilterError('You can only search within the last one month.');
      return;
    }

    const msDiff = to.getTime() - from.getTime();
    const dayDiff = Math.floor(msDiff / (24 * 60 * 60 * 1000));
    if (dayDiff >= HISTORY_WINDOW_DAYS) {
      setFilterError('Date range cannot exceed one month.');
      return;
    }

    setFilterError('');
    setDateFrom(nextFrom);
    setDateTo(nextTo);
  };

  useEffect(() => {
    if (!hasUserId) return;

    const run = async () => {
      setLoading(true);
      setError('');

      try {
        const [gamesRes, challengesRes, mpRes, chessRes, checkersRes, draughtsRes] = await Promise.all([
          gameAPI.getUserGames(),
          api.get('/challenges/history?limit=50'),
          multiplayerAPI.getGameHistory(userId),
          chessAPI.listHistory(50),
          checkersAPI.listHistory(50),
          draughtsAPI.listHistory(50)
        ]);

        setGames(Array.isArray(gamesRes.data) ? gamesRes.data : []);
        setChallenges(Array.isArray(challengesRes.data) ? challengesRes.data : []);

        const mpPayload = mpRes?.data;
        const mpList = Array.isArray(mpPayload?.games) ? mpPayload.games : Array.isArray(mpPayload) ? mpPayload : [];
        setMultiplayerGames(mpList);

        setChessHistory(Array.isArray(chessRes.data) ? chessRes.data : []);
        setCheckersHistory(Array.isArray(checkersRes.data) ? checkersRes.data : []);
        setDraughtsHistory(Array.isArray(draughtsRes.data) ? draughtsRes.data : []);
      } catch (e) {
        console.error('Failed to load history:', e);
        setError(e.response?.data?.error || e.message || 'Failed to load history');
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [hasUserId, userId]);

  const getResultBadge = (result) => {
    const lowerResult = (result || '').toLowerCase();
    if (lowerResult === 'win') {
      return <span className="result-badge win">🏆 Win</span>;
    }
    if (lowerResult === 'loss') {
      return <span className="result-badge loss">💔 Loss</span>;
    }
    if (lowerResult === 'draw') {
      return <span className="result-badge draw">⚖️ Draw</span>;
    }
    if (lowerResult === 'completed') {
      return <span className="result-badge completed">✓ Completed</span>;
    }
    if (lowerResult === 'pending') {
      return <span className="result-badge pending">⏳ Pending</span>;
    }
    return <span className="result-badge played">🎮 Played</span>;
  };

  const stats = useMemo(() => {
    const total = filteredItems.length;
    const byCategory = {};
    HISTORY_CATEGORY_ORDER.forEach(cat => {
      byCategory[cat] = filteredItems.filter(i => normalizeCategory(i.category) === cat).length;
    });
    return { total, byCategory };
  }, [filteredItems]);

  return (
    <div className="dashboard-container dashboard-redesign">
      <div className="animated-bg">
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
      </div>

      <div className="dashboard-content">
        {/* Header */}
        <div className="history-header">
          <div className="header-left">
            <div className="header-icon">📜</div>
            <div>
              <h1 className="page-title">Game History</h1>
              <p className="page-subtitle">Track all your gaming activity in one place</p>
            </div>
          </div>
          <button className="back-btn" onClick={() => navigate('/dashboard')}>
            ← Back to Dashboard
          </button>
        </div>

        {/* Stats Overview */}
        {!loading && !error && filteredItems.length > 0 && (
          <div className="stats-overview">
            <div className="stat-card total">
              <div className="stat-value">{stats.total}</div>
              <div className="stat-label">Total Games</div>
            </div>
            {HISTORY_CATEGORY_ORDER.map(cat => (
              stats.byCategory[cat] > 0 && (
                <div key={cat} className="stat-card" style={{ borderLeftColor: categoryColor(cat) }}>
                  <div className="stat-icon">{categoryIcon(cat)}</div>
                  <div className="stat-info">
                    <div className="stat-value">{stats.byCategory[cat]}</div>
                    <div className="stat-label">{categoryLabel(cat)}</div>
                  </div>
                </div>
              )
            ))}
          </div>
        )}

        {loading && (
          <div className="loading-state">
            <div className="spinner"></div>
            <span>Loading your history...</span>
          </div>
        )}

        {!loading && error && (
          <div className="error-alert">
            <span className="error-icon">⚠️</span>
            <span>{error}</span>
          </div>
        )}

        {!loading && !error && (
          <>
            {/* Filters Section */}
            <div className="filters-card">
              <div className="filters-row">
                <div className="search-wrapper">
                  <span className="search-icon">🔍</span>
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Search by game, player, ID..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                  {searchText && (
                    <button className="clear-search" onClick={() => setSearchText('')}>×</button>
                  )}
                </div>
                
                <div className="view-toggle">
                  <button 
                    className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
                    onClick={() => setViewMode('list')}
                  >
                    📋 List
                  </button>
                  <button 
                    className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                    onClick={() => setViewMode('grid')}
                  >
                    🔲 Grid
                  </button>
                </div>
              </div>

              <div className="filters-row">
                <div className="category-filters">
                  <button
                    className={`category-filter ${activeCategory === 'all' ? 'active' : ''}`}
                    onClick={() => setActiveCategory('all')}
                  >
                    All Games
                    {stats.total > 0 && <span className="filter-count">{stats.total}</span>}
                  </button>
                  {HISTORY_CATEGORY_ORDER.map((category) => {
                    const count = stats.byCategory[category];
                    if (count === 0) return null;
                    return (
                      <button
                        key={category}
                        className={`category-filter ${activeCategory === category ? 'active' : ''}`}
                        style={{ '--category-color': categoryColor(category) }}
                        onClick={() => setActiveCategory(category)}
                      >
                        <span className="filter-icon">{categoryIcon(category)}</span>
                        {categoryLabel(category)}
                        <span className="filter-count">{count}</span>
                      </button>
                    );
                  })}
                </div>
                
                <select 
                  className="sort-select"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="category">Sort by Category</option>
                </select>
              </div>

              <div className="filters-row">
                <div className="date-filters">
                  <div className="date-input-group">
                    <label>From</label>
                    <input
                      type="date"
                      className="date-input"
                      value={dateFrom}
                      min={getWindowStartDateInput()}
                      max={getTodayDateInput()}
                      onChange={(e) => applyDateRange(e.target.value, dateTo)}
                    />
                  </div>
                  <div className="date-input-group">
                    <label>To</label>
                    <input
                      type="date"
                      className="date-input"
                      value={dateTo}
                      min={getWindowStartDateInput()}
                      max={getTodayDateInput()}
                      onChange={(e) => applyDateRange(dateFrom, e.target.value)}
                    />
                  </div>
                </div>
                <div className="date-hint">
                  <span className="hint-icon">📅</span>
                  Last {HISTORY_WINDOW_DAYS} days only
                </div>
              </div>

              {filterError && (
                <div className="filter-error">
                  <span>⚠️</span> {filterError}
                </div>
              )}
            </div>

            {/* Results */}
            {filteredItems.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">🎮</div>
                <div className="empty-title">No games found</div>
                <div className="empty-desc">
                  {searchText || activeCategory !== 'all' || dateFrom !== getWindowStartDateInput() || dateTo !== getTodayDateInput()
                    ? 'Try adjusting your filters to see more results'
                    : 'Your game history will appear here once you start playing'}
                </div>
                {(searchText || activeCategory !== 'all' || dateFrom !== getWindowStartDateInput() || dateTo !== getTodayDateInput()) && (
                  <button 
                    className="clear-filters-btn"
                    onClick={() => {
                      setSearchText('');
                      setActiveCategory('all');
                      setDateFrom(getWindowStartDateInput());
                      setDateTo(getTodayDateInput());
                      setFilterError('');
                    }}
                  >
                    Clear All Filters
                  </button>
                )}
              </div>
            ) : viewMode === 'list' ? (
              <div className="history-list">
                {filteredItems.map((item) => {
                  const relativeTime = formatRelativeTime(item.date);
                  const category = normalizeCategory(item.category);
                  return (
                    <div key={item.key} className="history-item">
                      <div className="history-item-icon" style={{ backgroundColor: `${categoryColor(category)}20`, borderLeftColor: categoryColor(category) }}>
                        {categoryIcon(category)}
                      </div>
                      <div className="history-item-content">
                        <div className="history-item-header">
                          <div className="history-item-category" style={{ color: categoryColor(category) }}>
                            {categoryLabel(category)}
                          </div>
                          <div className="history-item-id">#{item.id}</div>
                          {getResultBadge(item.result)}
                        </div>
                        <div className="history-item-title">{item.title}</div>
                        <div className="history-item-subtitle">{item.subtitle}</div>
                        <div className="history-item-details">{item.details}</div>
                        <div className="history-item-footer">
                          <div className="history-item-date">
                            <span className="date-icon">📅</span>
                            {relativeTime || formatEventDate(item.date)}
                          </div>
                          <button 
                            className="view-details-btn"
                            onClick={() => navigate(item.viewPath)}
                          >
                            View Details →
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="history-grid">
                {filteredItems.map((item) => {
                  const relativeTime = formatRelativeTime(item.date);
                  const category = normalizeCategory(item.category);
                  return (
                    <div key={item.key} className="history-grid-item">
                      <div className="grid-item-header" style={{ borderTopColor: categoryColor(category) }}>
                        <div className="grid-item-category">
                          <span className="grid-category-icon">{categoryIcon(category)}</span>
                          <span className="grid-category-name" style={{ color: categoryColor(category) }}>
                            {categoryLabel(category)}
                          </span>
                        </div>
                        {getResultBadge(item.result)}
                      </div>
                      <div className="grid-item-content">
                        <div className="grid-item-title">{item.title}</div>
                        <div className="grid-item-subtitle">{item.subtitle}</div>
                        <div className="grid-item-details">{item.details}</div>
                      </div>
                      <div className="grid-item-footer">
                        <div className="grid-item-date">
                          {relativeTime || formatEventDate(item.date)}
                        </div>
                        <button 
                          className="view-btn-small"
                          onClick={() => navigate(item.viewPath)}
                        >
                          View
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Results Summary */}
            {filteredItems.length > 0 && (
              <div className="results-summary">
                Showing {filteredItems.length} of {historyItems.length} games
              </div>
            )}
          </>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default History;